import {
  createContext, useContext, useState, useEffect, useCallback, useMemo,
} from 'react';
import useSWR from 'swr';
import { API } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';

const KEY = 'mw-cart-v1';
const FULFIL_KEY = 'mw-cart-fulfilment-v1';
const REF_KEY = 'mw-referral-code-v1';
const CartContext = createContext(null);

const fetcher = (url) => fetch(`${API}${url}`).then((r) => r.json());

// Bulk discount defaults (mirrors backend; server-side is authoritative)
const BULK_KG = 10;
const BULK_PCT = 0.10;

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [fulfilment, setFulfilment] = useState({ mode: 'metro', slot_date: '', slot_window: '' });
  const [recurring, setRecurring] = useState(null); // null | 'weekly' | 'fortnightly' | 'monthly'
  const [referralCode, setReferralCodeState] = useState('');
  const setReferralCode = useCallback((code) => {
    const clean = (code || '').trim().toUpperCase();
    setReferralCodeState(clean);
    try {
      if (clean) window.localStorage.setItem(REF_KEY, clean);
      else window.localStorage.removeItem(REF_KEY);
    } catch { /* ignore */ }
  }, []);

  const { data: config } = useSWR('/shop/config', fetcher);
  const { data: slots } = useSWR('/shop/slots', fetcher);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setItems(parsed);
      }
      const fraw = window.localStorage.getItem(FULFIL_KEY);
      if (fraw) setFulfilment(JSON.parse(fraw));
      // Referral: URL ?ref=CODE wins, then localStorage
      const url = new URL(window.location.href);
      const urlRef = url.searchParams.get('ref');
      if (urlRef) {
        const clean = urlRef.trim().toUpperCase();
        setReferralCodeState(clean);
        try { window.localStorage.setItem(REF_KEY, clean); } catch { /* ignore */ }
      } else {
        const savedRef = window.localStorage.getItem(REF_KEY);
        if (savedRef) setReferralCodeState(savedRef);
      }
    } catch { /* ignore */ }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try { window.localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items, hydrated]);
  useEffect(() => {
    if (!hydrated) return;
    try { window.localStorage.setItem(FULFIL_KEY, JSON.stringify(fulfilment)); } catch { /* ignore */ }
  }, [fulfilment, hydrated]);

  const add = useCallback((product, qty) => {
    const step = Number(product.step_qty) || (product.unit === 'kg' ? 0.5 : 1);
    const min = Number(product.min_qty) || step;
    const q = qty ?? min;
    let wasNew = false;
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: +(i.quantity + q).toFixed(2) } : i
        );
      }
      wasNew = true;
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          category: product.category || '',
          image_url: product.image_url || '',
          price: Number(product.price) || 0,
          unit: product.unit || 'kg',
          step,
          min,
          quantity: q,
        },
      ];
    });
    // Only auto-open the drawer the first time a product enters the cart.
    // Subsequent quantity increments keep the product table interactive so
    // users can build a multi-item order without dismissing the overlay.
    if (wasNew) setDrawerOpen(true);
    // Fire analytics — never let it break the cart flow
    trackEvent('Add To Cart', {
      product: product.name,
      category: product.category || '',
      price: Number(product.price) || 0,
    });
  }, []);

  const setQty = useCallback((product_id, quantity) => {
    setItems((prev) => prev.map((i) => {
      if (i.product_id !== product_id) return i;
      const min = i.min || i.step || 0.5;
      const q = Math.max(min, Number(quantity) || min);
      return { ...i, quantity: +q.toFixed(2) };
    }));
  }, []);

  const increment = useCallback((product_id) => {
    setItems((prev) => prev.map((i) =>
      i.product_id === product_id ? { ...i, quantity: +(i.quantity + (i.step || 0.5)).toFixed(2) } : i
    ));
  }, []);

  const decrement = useCallback((product_id) => {
    setItems((prev) => prev.map((i) => {
      if (i.product_id !== product_id) return i;
      const min = i.min || i.step || 0.5;
      return { ...i, quantity: Math.max(min, +(i.quantity - (i.step || 0.5)).toFixed(2)) };
    }));
  }, []);

  const remove = useCallback((product_id) => {
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  }, []);

  const clear = useCallback(() => setItems([]), []);
  const openDrawer = useCallback(() => {
    window.dispatchEvent(new CustomEvent('mw-close-quote-drawer'));
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  useEffect(() => {
    const h = () => setDrawerOpen(false);
    window.addEventListener('mw-close-cart-drawer', h);
    return () => window.removeEventListener('mw-close-cart-drawer', h);
  }, []);

  const bulkKg = config?.bulk_threshold_kg ?? BULK_KG;
  const bulkPct = config?.bulk_discount_pct ?? BULK_PCT;

  // Per-line pricing including bulk discount
  const linesWithPricing = useMemo(() => items.map((i) => {
    const isBulk = i.unit === 'kg' && i.quantity >= bulkKg;
    const effectiveUnit = isBulk ? i.price * (1 - bulkPct) : i.price;
    const lineTotal = +(effectiveUnit * i.quantity).toFixed(2);
    const savings = isBulk ? +((i.price - effectiveUnit) * i.quantity).toFixed(2) : 0;
    return { ...i, isBulk, effectiveUnit, lineTotal, savings };
  }), [items, bulkKg, bulkPct]);

  const subtotal = useMemo(
    () => linesWithPricing.reduce((s, i) => s + i.lineTotal, 0),
    [linesWithPricing]
  );
  const totalSavings = useMemo(
    () => linesWithPricing.reduce((s, i) => s + i.savings, 0),
    [linesWithPricing]
  );

  const shippingRules = config?.shipping_rules;
  const shipping = useMemo(() => {
    if (!shippingRules) return 0;
    if (subtotal === 0) return 0;
    const rule = shippingRules[fulfilment.mode];
    if (!rule) return 0;
    if (rule.free_over && subtotal >= rule.free_over) return 0;
    return rule.fee;
  }, [subtotal, shippingRules, fulfilment.mode]);

  const total = subtotal + shipping;
  const meetsMin = subtotal >= (config?.min_order_total || 50);
  const slotChosen = Boolean(fulfilment.mode && fulfilment.slot_date && fulfilment.slot_window);
  const count = items.length;

  const setFulfilmentField = useCallback((patch) => {
    setFulfilment((f) => ({ ...f, ...patch }));
  }, []);

  const value = useMemo(() => ({
    items: linesWithPricing, count, subtotal, shipping, total, drawerOpen, config, slots,
    meetsMin, slotChosen, totalSavings, fulfilment, recurring, referralCode,
    add, remove, setQty, increment, decrement, clear, openDrawer, closeDrawer,
    setFulfilment, setFulfilmentField, setRecurring, setReferralCode,
  }), [linesWithPricing, count, subtotal, shipping, total, drawerOpen, config, slots,
       meetsMin, slotChosen, totalSavings, fulfilment, recurring, referralCode,
       add, remove, setQty, increment, decrement, clear, openDrawer, closeDrawer,
       setFulfilmentField, setReferralCode]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
};

export const formatAUD = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
